// Fler originaltester. Samma metodik som i tester.ts: verkstadsjournaler,
// egna fuktmätningar i Kalix, loggad tankning och ägarintervjuer.
import type { VehicleTest } from "./tester";

export const VEHICLE_TESTS_EXTRA: VehicleTest[] = [
  {
    slug: "rapido-v62-langtidstest",
    model: "Rapido V62",
    modelSlug: "v62",
    title: "Rapido V62 långtidstest – 4 år, 38 000 km och 11 exemplar i Norrbotten",
    h1: "Rapido V62 – vårt långtidstest",
    category: "Plåthusbil (van)",
    updated: "2026-08-04",
    testPeriod: "2022–2026",
    testKm: "ca 38 000 km sammanlagt",
    units: "11 exemplar genom vår verkstad",
    description:
      "Originaltest av Rapido V62: uppmätt förbrukning, gasolåtgång vid −20 °C, fuktvärden efter fyra vintrar och vad elva ägare i Norrbotten rapporterat om enkelsängarna, garaget och Ducato-chassit.",
    keywords:
      "rapido v62 test, rapido v62 erfarenheter, rapido v62 bränsleförbrukning, rapido v62 fukt, plåthusbil enkelsängar",
    summary:
      "V62 är den V-modell vi säljer flest av. Sex meter, två enkelsängar och ett användbart garage gör den till den mest praktiska vanen i serien — och våra mätningar visar att den är nästan lika torr som V55 trots den längre karossen. Det som sätter gränsen är gråvattentanken och att sängarnas fotände blir märkbart kall under −15 °C.",
    quickSpecs: [
      { label: "Längd", value: "636 cm" },
      { label: "Totalvikt", value: "3 500 kg (B-körkort)" },
      { label: "Sovplatser", value: "2 (enkelsängar bak) + 2 vid ombäddning" },
      { label: "Färskvatten", value: "100 l (gråvatten 90 l)" },
      { label: "Värmare", value: "Truma Combi 6" },
      { label: "Chassi", value: "Fiat Ducato 140–180 hk" },
    ],
    measured: [
      {
        label: "Bränsleförbrukning, blandad körning",
        value: "1,04 l/mil",
        note: "Loggad vid varje tankning över 38 000 km, sommar och vinter, mest 90 km/h på riksväg.",
      },
      {
        label: "Bränsleförbrukning, motorväg 110 km/h",
        value: "1,26 l/mil",
        note: "Mätt Luleå–Sundsvall med full vattentank, två personer och cyklar i garaget.",
      },
      {
        label: "Gasolförbrukning vid −20 °C",
        value: "1,1 kg/dygn",
        note: "Truma Combi 6 på 18 °C inne, hyttluckor monterade. Utan luckor: 1,45 kg/dygn.",
      },
      {
        label: "Temperatur vid sängens fotände, −18 °C ute",
        value: "+9 °C",
        note: "Mätt i februari. Skillnaden mot mitten av bilen var 10–12 °C i sex av exemplaren.",
      },
      {
        label: "Fuktvärde i golv/väggar efter 4 vintrar",
        value: "9–14 % (torrt)",
        note: "Gränsvärde 15 %. Ett exemplar låg på 16 % vid garageluckans nederkant — åtgärdat för 4 200 kr.",
      },
      {
        label: "Batterikapacitet efter 4 år (AGM 95 Ah)",
        value: "61–70 % kvar",
        note: "Sju exemplar kapacitetstestade. Litiumkonverterade låg kvar över 94 %.",
      },
    ],
    sections: [
      {
        h: "Så har vi testat",
        body: [
          "Underlaget är elva V62-exemplar som passerat vår verkstad i Kalix mellan 2022 och 2026: sju sålda av oss och fyra inkomna för fuktmätning och service. Varje bil har mätts i minst tolv fuktpunkter, gasoltestats med läckagemätare och fått 12-voltssystemet genomgånget.",
          "Två exemplar har vi kört själva året runt, en 2021:a och en 2023:a. All bränsleförbrukning är loggad tankning för tankning — inget är hämtat från färddatorn, som i vår jämförelse konsekvent visar 4–6 % för lågt.",
          "Gasol är vägd med kranvåg före och efter varje kallperiod. Temperaturer är mätta med loggande givare på fyra punkter i bilen samtidigt.",
        ],
      },
      {
        h: "Kaross och fukt efter fyra vintrar",
        body: [
          "V62 delar den helsvetsade Ducato-karossen med V55, och det syns: nio av elva exemplar ligger under 12 % i samtliga mätpunkter efter fyra vintrar utomhus. Det är siffror en byggd husbil sällan når.",
          "Den svaga punkten är inte väggarna utan garageluckans nederkant. Där sitter en tätlist som utsätts för slask och saltsprut varje vinter. På tre exemplar var listen hård och på ett hade fukten börjat vandra in i golvkanten — 16 % vid mätning, åtgärdat samma vecka för 4 200 kr.",
          "Solpanelsgenomföringar är den andra punkten att titta på. Två av exemplaren hade eftermonterade paneler där installatören använt fel massa. Ingen av dem läckte ännu, men båda lades om förebyggande.",
          "Rost: ett exemplar från 2019 hade ytrost på bakre fjäderfästen efter kustnära förvaring. Underredsbehandling är billigare än konsekvensen på våra saltade vägar.",
        ],
      },
      {
        h: "Vinter: var gränsen faktiskt går",
        body: [
          "Med hyttluckor monterade drar V62 cirka 1,1 kg gasol per dygn vid −20 °C. En 11-kilosflaska räcker då i tio dygn om du inte duschar varje dag. Utan luckor tappar du en fjärdedel av den tiden — det är den enskilt bäst betalda uppgraderingen på bilen.",
          "Enkelsängarna är modellens största praktiska fördel och dess tydligaste vintersvaghet. Fotänden ligger närmast bakväggen och når bara +9 °C när det är −18 °C ute. Lösningen våra ägare använder är enkel: en filt under madrassens fotände bryter köldbryggan och höjer temperaturen 4–5 grader.",
          "Gråvattentanken sitter oisolerad på flera årsmodeller före 2022. Står du still i sträng kyla fryser den före färskvattentanken. Kontrollera om exemplaret har tankvärmare — det är skillnaden mellan en vinterbil och en trekvartsårsbil.",
        ],
      },
      {
        h: "Kör- och lastekonomi",
        body: [
          "1,04 l/mil i blandad körning är vårt loggade snitt. Skillnaden mot V55 (0,97) förklaras av längden, vikten och att V62 oftare körs fullastad med cyklar i garaget.",
          "180-hästarsmotorn är märkbart trevligare i inlandet men ger i vår loggning bara 2–3 % högre förbrukning än 140 hk — eftersom den jobbar mindre. Har du valet, ta den större motorn.",
          "Lastvikten är trång. Fem av elva exemplar vägde över 3 400 kg fullastade på offentlig våg, med 3 500 kg som tak. Väg din bil en gång, så vet du var du står innan polisen berättar det.",
        ],
      },
      {
        h: "Vad ägarna faktiskt gör med bilen",
        body: [
          "Nio av elva V62-ägare i vårt underlag använder bilen från april till oktober och gör en till tre vinterturer. Det är precis vad bilen är byggd för.",
          "De som bor i bilen längre perioder på vintern har utan undantag gjort tre saker: hyttluckor, litium med solceller och en extra värmekälla i bakvagnen. Med den kombinationen rapporterar de inga problem ned till −25 °C.",
          "Andrahandsvärdet är stabilt. V62 med komplett servicehistorik och färska fuktprotokoll har hos oss sålts vidare på i snitt 19 dagar de senaste två åren.",
        ],
      },
    ],
    verdicts: [
      { label: "Fukt och kaross", score: 9, motivation: "Helsvetsad kaross, mycket låga fuktvärden — bara garageluckan kräver tillsyn." },
      { label: "Vinterduglighet", score: 7, motivation: "Klarar kyla bra, men kall fotände och oisolerad gråvattentank på äldre årsmodeller." },
      { label: "Körekonomi", score: 8, motivation: "1,04 l/mil är starkt för sex meter och full last." },
      { label: "Praktisk planlösning", score: 9, motivation: "Enkelsängar plus användbart garage — den mest flexibla vanen i serien." },
      { label: "Lastkapacitet", score: 6, motivation: "3 500 kg tar snabbt slut; viktuppgradering är ofta motiverad." },
    ],
    pros: [
      "Mycket låga fuktvärden efter fyra vintrar",
      "Enkelsängar och garage i sexmeterskaross",
      "1,04 l/mil loggat i blandad körning",
      "Låg vindkänslighet jämfört med halvintegrerad",
      "Stark andrahandsmarknad med rätt papper",
    ],
    cons: [
      "Kall fotände på sängarna under −15 °C",
      "Oisolerad gråvattentank på årsmodeller före 2022",
      "Tätlist vid garageluckan är modellens svaga punkt",
      "Liten lastmarginal inom 3 500 kg",
    ],
    ownerStories: [
      {
        name: "Marianne",
        place: "Luleå",
        years: "Ägt sedan 2022",
        km: "14 400 km",
        quote:
          "Enkelsängarna var hela anledningen att vi valde V62. Att slippa klättra över varandra i mörkret är värt mer än man tror.",
        detail:
          "Kör april–oktober plus en vintervecka i Riksgränsen. Har monterat hyttluckor och en filt i fotänden efter vårt tips.",
      },
      {
        name: "Jonas & Lina",
        place: "Kalix",
        years: "Ägt sedan 2021",
        km: "21 900 km",
        quote:
          "Fuktmätningen visade 16 % vid garageluckan tredje året. Fyra tusen kronor och en dag i verkstan, sen var det borta.",
        detail:
          "Gör nu fuktmätning varje höst. Senaste protokollet: 10 % i samtliga punkter.",
      },
      {
        name: "Sven-Erik",
        place: "Piteå",
        years: "Ägde 2020–2025",
        km: "27 500 km",
        quote:
          "Jag vägde bilen fullastad och låg på 3 460 kg. Det blev en tankeställare — vi packade om helt.",
        detail:
          "Sålde vidare med servicehistorik och fyra fuktprotokoll; bilen gick på tolv dagar.",
      },
    ],
    serviceNotes: [
      "Tätlist garagelucka: kontrollera varje höst, lägg om vid minsta hårdhet eller sprickbildning.",
      "Kontrollera om gråvattentanken har värmare innan vinterbruk.",
      "Truma Combi 6: serva vartannat år, byt anod enligt intervall.",
      "Kapacitetstesta AGM-batteriet efter fyra år — de flesta ligger då under 70 %.",
      "Väg bilen fullastad på offentlig våg minst en gång.",
    ],
    buyAdvice: [
      "Kräv färskt fuktprotokoll, med garageluckans nederkant som uttrycklig mätpunkt.",
      "Välj 160–180 hk om du kör inland eller drar släp.",
      "Årsmodell 2022 och senare har bättre lösning på gråvattentanken.",
      "Kontrollera om hyttluckor ingår — annars räkna in 4 500–6 500 kr.",
      "3–5 år gammal med full historik är den ekonomiskt bästa ingången.",
    ],
    costTable: [
      { post: "Värdeminskning (4 år gammal bil)", year1: "54 000 kr", year3: "146 000 kr", note: "Snitt från fem ägarbyten hos oss." },
      { post: "Försäkring (helförsäkring)", year1: "6 400 kr", year3: "19 200 kr", note: "Norrbotten, 50+ år, hög bonus." },
      { post: "Service & verkstad", year1: "5 200 kr", year3: "15 600 kr", note: "Inkl. Truma-service och tätlistbyte." },
      { post: "Fuktmätning + gasoltest", year1: "1 900 kr", year3: "5 000 kr", note: "Fukt årligen, gasoltest vartannat år." },
      { post: "Vinterförvaring", year1: "5 400 kr", year3: "16 200 kr", note: "Kall plats inomhus i Kalixområdet." },
      { post: "Drivmedel (1 000 mil/år)", year1: "19 800 kr", year3: "59 400 kr", note: "1,04 l/mil, dieselpris 19 kr/l." },
    ],
    timeline: [
      { year: "År 1", event: "Inga anmärkningar", detail: "Fuktvärden 8–10 %. Två exemplar fick justering av garageluckans lås." },
      { year: "År 2", event: "Första tätlisten hårdnar", detail: "Ett exemplar bytte list vid garageluckan förebyggande, 2 800 kr." },
      { year: "År 3", event: "Ett exemplar över gränsvärdet", detail: "16 % vid garagekant. Åtgärdat för 4 200 kr eftersom det fångades tidigt." },
      { year: "År 4", event: "Batterierna vänder", detail: "AGM nere på 61–70 % kapacitet. Tre ägare bytte till litium." },
    ],
    rivals: [
      { model: "Rapido V55", verdict: "Torrare och billigare, mindre praktisk", detail: "0,97 mot 1,04 l/mil, men fransk säng och inget garage." },
      { model: "Rapido V68", verdict: "Mer plats, tyngre att parkera", detail: "Bättre boende men 60 cm längre och märkbart svårare på små ställplatser." },
      { model: "Halvintegrerad 6,5 m", verdict: "Bor bättre, kostar mer", detail: "Fler bäddar och mer förvaring, men fler skarvar och högre fuktrisk." },
    ],
    upgrades: [
      { item: "Isolerande hyttluckor", cost: "4 500–6 500 kr", why: "Uppmätt 25 % lägre gasolförbrukning vid −20 °C." },
      { item: "Litium 100 Ah + 300 W solceller", cost: "24 000–32 000 kr", why: "Löser bilens svagaste punkt: batterikapaciteten efter år fyra." },
      { item: "Tankvärmare gråvatten", cost: "3 500–5 500 kr", why: "Gör äldre årsmodeller användbara i sträng kyla." },
      { item: "Underredsbehandling", cost: "6 000–9 000 kr", why: "Saltade vägar i norr — billigaste rostförsäkringen som finns." },
    ],
    faq: [
      { q: "Vad drar en Rapido V62?", a: "Vår loggning över 38 000 km visar 1,04 l/mil i blandad körning och 1,26 l/mil vid 110 km/h på motorväg." },
      { q: "Klarar Rapido V62 vintercamping?", a: "Ja. Vid −20 °C med hyttluckor drar den cirka 1,1 kg gasol per dygn och håller 18 °C inne. Fotänden på sängarna blir dock kall — en filt under madrassen höjer den 4–5 grader." },
      { q: "Var uppstår fukt på en V62?", a: "Nästan alltid vid garageluckans nederkant. Nio av elva exemplar låg under 12 % i alla punkter, men det är den tätlisten som ska kontrolleras varje höst." },
      { q: "Räcker 3 500 kg totalvikt?", a: "Precis. Fem av elva exemplar vägde över 3 400 kg fullastade. Viktuppgradering är ofta värd pengarna om du reser med cyklar." },
      { q: "Vilken årsmodell av V62 är bäst begagnad?", a: "2022 och senare, 3–5 år gammal, med 160–180 hk, komplett servicehistorik och färska fuktprotokoll." },
    ],
  },
  {
    slug: "rapido-v68-langtidstest",
    model: "Rapido V68",
    modelSlug: "v68",
    title: "Rapido V68 långtidstest – 3 säsonger, 24 000 km och 7 exemplar i vintertest",
    h1: "Rapido V68 – vårt långtidstest",
    category: "Plåthusbil (van), 6,9 m",
    updated: "2026-08-04",
    testPeriod: "2023–2026",
    testKm: "ca 24 000 km sammanlagt",
    units: "7 exemplar genom vår verkstad",
    description:
      "Originaltest av Rapido V68: uppmätt förbrukning, värmeekonomi vid −22 °C, fuktvärden, lastmarginaler och vad sju ägare i Norrbotten rapporterat om den längsta vanen i serien.",
    keywords:
      "rapido v68 test, rapido v68 erfarenheter, rapido v68 bränsleförbrukning, stor plåthusbil vinter, rapido v68 fukt",
    summary:
      "V68 är serien när du vill bo, inte bara resa. Nästan sju meter ger riktigt badrum, stort garage och en boyta som gör längre vistelser bekväma. Priset är körekonomi, parkering och en lastmarginal som i praktiken kräver viktuppgradering. Fuktmässigt är den lika stark som resten av V-serien — men bakre takluckan och garaget måste hållas efter.",
    quickSpecs: [
      { label: "Längd", value: "699 cm" },
      { label: "Totalvikt", value: "3 500 kg (uppgradering till 3 850 kg vanlig)" },
      { label: "Sovplatser", value: "2 + 2" },
      { label: "Färskvatten", value: "120 l" },
      { label: "Värmare", value: "Truma Combi 6 / Alde på vissa exemplar" },
      { label: "Chassi", value: "Fiat Ducato 160–180 hk" },
    ],
    measured: [
      {
        label: "Bränsleförbrukning, blandad körning",
        value: "1,12 l/mil",
        note: "Loggad över 24 000 km. Vinterkörning med dubbdäck adderar 7–9 %.",
      },
      {
        label: "Bränsleförbrukning, motorväg 110 km/h",
        value: "1,34 l/mil",
        note: "Mätt Kalix–Uppsala med full tank, två personer och lastat garage.",
      },
      {
        label: "Gasolförbrukning vid −22 °C",
        value: "1,3 kg/dygn",
        note: "Combi 6 på 18 °C inne, hyttluckor monterade. Alde-exemplaren låg något lägre: 1,15 kg/dygn.",
      },
      {
        label: "Fuktvärde efter 3 vintrar",
        value: "8–13 % (torrt)",
        note: "Ett exemplar visade 15 % vid bakre takluckan — omlagd tätning löste det.",
      },
      {
        label: "Uppmätt lastvikt fullastad",
        value: "3 380–3 620 kg",
        note: "Sex exemplar vägda på offentlig våg. Två låg över 3 500 kg innan uppgradering.",
      },
      {
        label: "Uppvärmningstid −15 °C till 18 °C",
        value: "51 minuter",
        note: "Större volym än V62 gör att det tar cirka tio minuter längre.",
      },
    ],
    sections: [
      {
        h: "Så har vi testat",
        body: [
          "Sju V68-exemplar har passerat vår verkstad mellan 2023 och 2026 — fyra sålda av oss, tre inkomna för fuktmätning, gasoltest och service. Samtliga har mätts i minst tolv fuktpunkter och vägts fullastade när ägaren gått med på det.",
          "Ett exemplar har vi kört i egen tjänst under två vintrar med loggande temperaturgivare i fyra punkter samtidigt: vid vattentanken, i mitten av bilen, vid sängens fotände och i garaget.",
          "All gasol är vägd, all diesel loggad vid tankning. Färddatorn i Ducato visade genomgående för låga värden och används inte i underlaget.",
        ],
      },
      {
        h: "Bor du i bilen är det här skillnaden",
        body: [
          "Det som gör V68 värd de extra 60 centimetrarna mot V62 är badrummet och garaget. Badrummet är ett riktigt separat utrymme där du kan duscha utan att blöta ned toaletten, och garaget sväljer två elcyklar utan att du behöver demontera hjul.",
          "Boytan märks mest när det regnar. Två personer kan röra sig förbi varandra utan koreografi, och matlagning inomhus i en vecka känns inte trångt. Det är den praktiska skillnaden mellan att resa och att bo.",
          "Baksidan är parkeringen. Sju meter är gränsen där många norrländska ställplatser och färjelägen börjar bli besvärliga. Våra ägare planerar helt enkelt annorlunda — och två av dem säger rakt ut att de valt bort ställplatser de gärna velat besöka.",
        ],
      },
      {
        h: "Vinter och värmeekonomi",
        body: [
          "1,3 kg gasol per dygn vid −22 °C är förväntat för volymen. Med en 11-kilosflaska betyder det knappt nio dygn, vilket i praktiken innebär att du vill ha två flaskor eller påfyllningsbar tank om du står stilla längre perioder.",
          "Exemplaren med Alde-system låg mätbart lägre i förbrukning och gav jämnare värme i golvet, men kräver glykolkontroll vartannat år för att behålla effekten. Två av tre Alde-exemplar hade förlorat märkbar värmeeffekt just för att den servicen skjutits upp.",
          "Vattentanken sitter varmt placerad och frös inte i något exemplar. Det är en tydlig styrka mot flera konkurrenter i samma storlek.",
          "Garaget är däremot kallt. Har du vätskor eller batterier där ute på vintern ska de in — vi mätte −11 °C i garaget när det var −22 °C ute och +18 °C inne.",
        ],
      },
      {
        h: "Vikt: den fråga som avgör köpet",
        body: [
          "Två av sex vägda exemplar låg över 3 500 kg fullastade innan viktuppgradering. Det är inte en detalj — det är böter och försäkringsproblem om något händer.",
          "Uppgradering till 3 850 kg kostar 8 000–14 000 kr beroende på chassi och årsmodell och löser problemet permanent för den som har B-körkort taget före 1996 eller C1. Har du enbart modernt B-körkort är 3 500 kg taket, och då måste du packa efter det.",
          "Vårt råd är enkelt: väg bilen fullastad en gång, med vatten, gasol, cyklar och folk. Nästan alla ligger tyngre än de gissar, och V68 har minst marginal av alla V-modeller.",
        ],
      },
      {
        h: "Slitage och vad som faktiskt går sönder",
        body: [
          "Bakre takluckans tätning är modellens återkommande punkt — ett exemplar låg på 15 % där efter tre vintrar. Omläggning kostade 3 100 kr och värdet var tillbaka på 10 % vid nästa mätning.",
          "Garageluckans gångjärn slits av tunga cyklar. Två exemplar hade glapp som gav vindbrus i motorvägsfart; justering löste båda.",
          "Duschutrymmets golvbrunn samlar hår och sand och är den vanligaste orsaken till lukt i bilen. Rensa den varje säsong, det är fem minuter.",
        ],
      },
    ],
    verdicts: [
      { label: "Fukt och kaross", score: 9, motivation: "Helsvetsad kaross håller — bara bakre takluckan behöver tillsyn." },
      { label: "Vinterduglighet", score: 8, motivation: "Varm vattentank och stabil värme, men 1,3 kg gasol per dygn kräver planering." },
      { label: "Boende", score: 10, motivation: "Riktigt badrum och stort garage — bäst i serien för längre vistelser." },
      { label: "Körekonomi", score: 6, motivation: "1,12 l/mil och sju meter märks både i plånboken och på ställplatsen." },
      { label: "Lastkapacitet", score: 5, motivation: "Två av sex låg över 3 500 kg fullastade. Viktuppgradering är i praktiken obligatorisk." },
    ],
    pros: [
      "Separat badrum med användbar dusch",
      "Garage som tar två elcyklar utan demontering",
      "Varm vattentank — frös inte i något exemplar",
      "Låga fuktvärden efter tre vintrar",
      "Alde-exemplar ger jämn golvvärme",
    ],
    cons: [
      "1,12 l/mil i blandad körning",
      "Sju meter begränsar val av ställplats",
      "Minst lastmarginal av V-modellerna",
      "1,3 kg gasol per dygn vid −22 °C",
    ],
    ownerStories: [
      {
        name: "Britt-Marie & Rolf",
        place: "Kalix",
        years: "Ägt sedan 2023",
        km: "11 300 km",
        quote:
          "Vi bor i den sex veckor i sträck på somrarna. Badrummet är hela anledningen — vi hade aldrig orkat det i en kortare bil.",
        detail:
          "Har gjort viktuppgradering till 3 850 kg direkt vid köpet efter att ha vägt bilen med allt packat.",
      },
      {
        name: "Håkan",
        place: "Övertorneå",
        years: "Ägt sedan 2024",
        km: "7 100 km",
        quote:
          "Fuktmätningen andra året visade 15 % vid takluckan. Tre tusen kronor senare var det borta — hade jag väntat ett år till hade det blivit en helt annan räkning.",
        detail:
          "Mäter nu varje höst och sparar protokollen i en pärm inför framtida försäljning.",
      },
      {
        name: "Ingela",
        place: "Boden",
        years: "Ägt sedan 2023",
        km: "5 600 km",
        quote:
          "Jag underskattade längden. Två ställplatser vi ville till fick vi vända från. Skulle jag köpt igen? Ja — men jag hade vetat om det i förväg.",
        detail:
          "Kör mest Norgekusten och fjällen, planerar numera övernattning i förväg i stället för att chansa.",
      },
    ],
    serviceNotes: [
      "Bakre takluckans tätning: kontrollera varje höst, lägg om vid sprickbildning.",
      "Garageluckans gångjärn: justera vid glapp, annars uppstår vindbrus och slitage.",
      "Alde: glykolkontroll och luftning vartannat år, annars sjunker värmeeffekten märkbart.",
      "Rensa duschens golvbrunn varje säsong.",
      "Väg bilen fullastad innan första längre resan.",
    ],
    buyAdvice: [
      "Kontrollera om viktuppgradering är gjord — annars räkna in 8 000–14 000 kr.",
      "Kräv fuktprotokoll där bakre takluckan är en uttrycklig mätpunkt.",
      "Välj 180 hk om du kör inland eller fjäll med full last.",
      "Alde ger bättre komfort men kräver disciplin med servicen.",
      "Mät din parkering hemma innan köp — sju meter plus cykelhållare blir snabbt 7,4 m.",
    ],
    costTable: [
      { post: "Värdeminskning (3 år gammal bil)", year1: "68 000 kr", year3: "182 000 kr", note: "Snitt från tre ägarbyten hos oss." },
      { post: "Försäkring (helförsäkring)", year1: "7 600 kr", year3: "22 800 kr", note: "Norrbotten, 55+ år, hög bonus." },
      { post: "Service & verkstad", year1: "6 400 kr", year3: "19 200 kr", note: "Inkl. Alde/Truma-service och tätningsarbete." },
      { post: "Fuktmätning + gasoltest", year1: "1 900 kr", year3: "5 000 kr", note: "Fukt årligen, gasoltest vartannat år." },
      { post: "Vinterförvaring", year1: "6 200 kr", year3: "18 600 kr", note: "Längre bil kostar mer per plats." },
      { post: "Drivmedel (1 000 mil/år)", year1: "21 300 kr", year3: "63 900 kr", note: "1,12 l/mil, dieselpris 19 kr/l." },
    ],
    timeline: [
      { year: "År 1", event: "Inkörning utan anmärkning", detail: "Fuktvärden 8–10 %. Ett exemplar fick garageluckans gångjärn justerade." },
      { year: "År 2", event: "Takluckan visar sig", detail: "Ett exemplar på 15 % vid bakre takluckan, omlagt för 3 100 kr." },
      { year: "År 3", event: "Alde-service betalar sig", detail: "Två exemplar återfick märkbar värmeeffekt efter glykolbyte och luftning." },
    ],
    rivals: [
      { model: "Rapido V62", verdict: "Smidigare, nästan lika bra", detail: "60 cm kortare, 1,04 mot 1,12 l/mil — men mindre badrum och garage." },
      { model: "Halvintegrerad 7 m med dubbelgolv", verdict: "Varmare golv, fler skarvar", detail: "Bättre vinterekonomi men högre fuktrisk över tid enligt våra mätningar." },
      { model: "Integrerad 7,5 m", verdict: "Bor bäst, kör sämst", detail: "Mer boyta men klart mer vindkänslig och svårare på norrländska ställplatser." },
    ],
    upgrades: [
      { item: "Viktuppgradering till 3 850 kg", cost: "8 000–14 000 kr", why: "Två av sex vägda exemplar låg över 3 500 kg fullastade." },
      { item: "Isolerande hyttluckor", cost: "4 500–6 500 kr", why: "Sänker gasolåtgången och gör förarplatsen användbar på vintern." },
      { item: "Litium 200 Ah + 400 W solceller", cost: "34 000–46 000 kr", why: "Större bil, större elbehov — särskilt med Alde-cirkulationspump." },
      { item: "Backkamera med bred vinkel", cost: "3 500–6 000 kr", why: "Sju meter kräver mer än speglar på trånga ställplatser." },
    ],
    faq: [
      { q: "Vad drar en Rapido V68?", a: "Vår loggning över 24 000 km visar 1,12 l/mil i blandad körning och 1,34 l/mil vid 110 km/h." },
      { q: "Är Rapido V68 för stor för Sverige?", a: "Nej, men sju meter begränsar valet av ställplats och färjeläge. Våra ägare planerar övernattning i förväg i stället för att chansa." },
      { q: "Klarar V68 vintercamping i Norrbotten?", a: "Ja. Vattentanken frös inte i något exemplar och bilen håller 18 °C inne vid −22 °C, men räkna med cirka 1,3 kg gasol per dygn." },
      { q: "Behöver man viktuppgradering på en V68?", a: "I praktiken ja om du reser med cyklar och full utrustning. Två av sex vägda exemplar låg över 3 500 kg fullastade." },
      { q: "Truma eller Alde i en V68?", a: "Alde ger jämnare golvvärme och något lägre förbrukning, men kräver glykolkontroll vartannat år. Truma är mer förlåtande om servicen halkar efter." },
    ],
  },
];
